import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Edit3, X, Beaker, ChevronLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const UNITS = [
  { key: "cl", label: "cl" },
  { key: "ml", label: "ml" },
  { key: "cs", label: "c. à soupe" },
  { key: "cc", label: "c. à café" },
  { key: "piece", label: "pièce(s)" },
  { key: "g", label: "gramme(s)" },
  { key: "trait", label: "trait" },
  { key: "goutte", label: "goutte(s)" },
];

export default function IngredientsAdmin() {
  const [ingredients, setIngredients] = useState([]);
  const [cocktails, setCocktails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // ingredient row being edited (null = closed)
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", unit: "cl", category: "", description: "" });
  const [recipeCocktail, setRecipeCocktail] = useState(null); // cocktail whose recipe modal is open

  const load = async () => {
    setLoading(true);
    try {
      const [ing, cock] = await Promise.all([
        api.get("/admin/ingredients"),
        api.get("/cocktails"),
      ]);
      setIngredients(ing.data);
      setCocktails(cock.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/ingredients/${editing.id}`, form);
        toast.success("Ingrédient mis à jour");
      } else {
        await api.post("/admin/ingredients", form);
        toast.success("Ingrédient créé");
      }
      setEditing(null); setCreating(false);
      setForm({ name: "", unit: "cl", category: "", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const del = async (ing) => {
    if (!window.confirm(`Supprimer « ${ing.name} » ?`)) return;
    try {
      await api.delete(`/admin/ingredients/${ing.id}`);
      toast.success("Ingrédient supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const startEdit = (ing) => {
    setEditing(ing);
    setCreating(false);
    setForm({
      name: ing.name, unit: ing.unit, category: ing.category || "",
      description: ing.description || "",
    });
  };

  const byCategory = useMemo(() => {
    const m = {};
    for (const i of ingredients) {
      const k = i.category || "Sans catégorie";
      (m[k] ||= []).push(i);
    }
    // Sort here (once per ingredients change) instead of at each render.
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [ingredients]);

  return (
    <div className="min-h-screen p-4 sm:p-6" data-testid="admin-ingredients-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <a href="/admin" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Tableau de bord
            </a>
            <h1 className="font-display text-3xl mt-1 flex items-center gap-2">
              <Beaker className="w-6 h-6 text-primary" /> Ingrédients & Recettes
            </h1>
            <p className="text-sm text-muted-foreground">Bill of Materials — utilisé par l'écran barman.</p>
          </div>
          <Button
            onClick={() => { setCreating(true); setEditing(null); setForm({ name: "", unit: "cl", category: "", description: "" }); }}
            className="rounded-full"
            data-testid="admin-ing-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouvel ingrédient
          </Button>
        </div>

        {/* Form modal */}
        {(creating || editing) && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
               onClick={() => { setCreating(false); setEditing(null); }}>
            <div
              className="bg-card border border-border rounded-3xl max-w-md w-full p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-ing-form-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">{editing ? "Modifier" : "Créer"} un ingrédient</h2>
                <button onClick={() => { setCreating(false); setEditing(null); }} className="text-muted-foreground hover:text-primary">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Nom</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                         maxLength={80} className="h-11 rounded-xl" data-testid="admin-ing-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Unité par défaut</label>
                    <select
                      value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      className="mt-1 w-full h-11 rounded-xl bg-background border border-border px-3"
                      data-testid="admin-ing-unit"
                    >
                      {UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Catégorie</label>
                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                           maxLength={40} placeholder="Alcool, Jus, Sirop…" className="h-11 rounded-xl" data-testid="admin-ing-category" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes (facultatif)</label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                         maxLength={280} className="h-11 rounded-xl" data-testid="admin-ing-desc" />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button variant="ghost" onClick={() => { setCreating(false); setEditing(null); }} className="flex-1 rounded-full">
                  Annuler
                </Button>
                <Button onClick={save} className="flex-1 rounded-full" data-testid="admin-ing-save-btn">
                  <Save className="w-4 h-4 mr-2" /> {editing ? "Enregistrer" : "Créer"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Ingredients list */}
            <section>
              <h2 className="font-display text-xl mb-3">Ingrédients ({ingredients.length})</h2>
              {ingredients.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
                  Aucun ingrédient. Commencez par créer « Rhum blanc », « Citron vert », etc.
                </div>
              ) : (
                <div className="space-y-4">
                  {byCategory.map(([cat, items]) => (
                    <div key={cat}>
                      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{cat}</div>
                      <div className="rounded-2xl border border-border bg-card divide-y divide-border/60">
                        {items.map((i) => (
                          <div key={i.id} className="flex items-center gap-3 p-3" data-testid={`admin-ing-row-${i.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{i.name}</div>
                              {i.description && <div className="text-xs text-muted-foreground truncate">{i.description}</div>}
                            </div>
                            <span className="text-xs bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5">{i.unit}</span>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(i)} data-testid={`admin-ing-edit-${i.id}`}>
                              <Edit3 className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(i)} data-testid={`admin-ing-del-${i.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Cocktail recipes */}
            <section>
              <h2 className="font-display text-xl mb-3">Cocktails ({cocktails.length})</h2>
              <div className="rounded-2xl border border-border bg-card divide-y divide-border/60">
                {cocktails.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setRecipeCocktail(c)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-primary/5"
                    data-testid={`admin-cocktail-recipe-btn-${c.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {Array.isArray(c.recipe) && c.recipe.length > 0
                          ? `${c.recipe.length} ingrédient(s) — ${c.recipe.map(r => r.name).slice(0, 3).join(", ")}`
                          : "Recette non renseignée"}
                      </div>
                    </div>
                    <Edit3 className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>

      {recipeCocktail && (
        <RecipeEditorModal
          cocktail={recipeCocktail}
          ingredients={ingredients}
          onClose={() => setRecipeCocktail(null)}
          onSaved={() => { setRecipeCocktail(null); load(); }}
        />
      )}
    </div>
  );
}


function RecipeEditorModal({ cocktail, ingredients, onClose, onSaved }) {
  const [rows, setRows] = useState(() =>
    (cocktail.recipe || []).map((r, i) => ({
      _rowId: `r-${i}-${r.ingredient_id}`,
      ingredient_id: r.ingredient_id,
      quantity: r.quantity,
    })),
  );
  const [saving, setSaving] = useState(false);

  const add = () => setRows((r) => [
    ...r,
    { _rowId: `r-${Date.now()}-${r.length}`,
      ingredient_id: ingredients[0]?.id || "", quantity: 1 },
  ]);
  const remove = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const update = (idx, patch) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/ingredients/cocktails/${cocktail.id}/recipe`, {
        recipe: rows
          .filter((r) => r.ingredient_id && r.quantity > 0)
          .map((r) => ({ ingredient_id: r.ingredient_id, quantity: Number(r.quantity) })),
      });
      toast.success("Recette enregistrée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally { setSaving(false); }
  };

  const ingLookup = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-3xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-recipe-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Recette</div>
            <h2 className="font-display text-2xl">{cocktail.name}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {ingredients.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
            Ajoutez d'abord des ingrédients à la liste (bouton « Nouvel ingrédient »).
          </div>
        ) : (
          <div className="space-y-3">
            {rows.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">Aucun ingrédient — cliquez sur « Ajouter » ci-dessous.</div>
            )}
            {rows.map((row, idx) => (
              <div key={row._rowId} className="flex gap-2 items-center" data-testid={`recipe-row-${idx}`}>
                <select
                  value={row.ingredient_id}
                  onChange={(e) => update(idx, { ingredient_id: e.target.value })}
                  className="flex-1 h-11 rounded-xl bg-background border border-border px-3 text-sm"
                  data-testid={`recipe-row-${idx}-ing`}
                >
                  {ingredients.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
                <Input
                  type="number" step="0.1" min="0.1"
                  value={row.quantity}
                  onChange={(e) => update(idx, { quantity: e.target.value })}
                  className="w-20 h-11 rounded-xl"
                  data-testid={`recipe-row-${idx}-qty`}
                />
                <span className="text-xs text-muted-foreground w-14">{ingLookup[row.ingredient_id]?.unit || "—"}</span>
                <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => remove(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={add} className="w-full rounded-full" data-testid="recipe-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un ingrédient
            </Button>
          </div>
        )}

        <div className="flex gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="flex-1 rounded-full">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 rounded-full" data-testid="recipe-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
